import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthContext';
import notificacionService from '../services/notificacionService';
import imgDesconectado from '../assets/img/whatsapp-desconectado.png';

const POLL_MS = 60000;

function WhatsAppDesconexionAlert() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [alerta, setAlerta] = useState(null);
    const procesandoRef = useRef(false);
    const toastMostradoRef = useRef(false);

    const esPrivilegiado = user && (user.rol === 'ADMIN' || user.rol === 'PROGRAMADOR');

    useEffect(() => {
        let cancelado = false;

        const chequear = async () => {
            try {
                const res = await notificacionService.getPendientes();
                const lista = Array.isArray(res?.data) ? res.data : [];
                // Se queda con TODAS las pendientes, no solo con la primera: si por lo que sea
                // hay varias acumuladas, cerrar el modal las tiene que atender a todas de una.
                // Antes marcaba una sola por cierre y el modal reaparecía tantas veces como
                // avisos hubiera (llegaron a ser 24 por usuario).
                const pendientes = lista.filter(n => n.origen === 'WHATSAPP_DESCONEXION' && !n.enviado);
                if (!cancelado) setAlerta(pendientes.length > 0 ? { ...pendientes[0], ids: pendientes.map(n => n.id) } : null);
            } catch (e) {
                // silencioso — no bloquear la app si falla el poll
            }
        };

        chequear();
        const interval = setInterval(chequear, POLL_MS);
        return () => { cancelado = true; clearInterval(interval); };
    }, []);

    const cerrar = async () => {
        if (!alerta || procesandoRef.current) return;
        procesandoRef.current = true;
        try {
            // marcarComoLeida pone enviado=true (para que este mismo modal no la vuelva a
            // mostrar); marcarComoLeidaUsuario pone leido=true (lo que usa la campana para el
            // contador de no leídas y la lista). Sin las dos, cerrar el modal no sacaba el aviso
            // de la campana — quedaba mostrado ahí como si nunca se hubiera atendido.
            const ids = alerta.ids && alerta.ids.length ? alerta.ids : [alerta.id];
            await Promise.all(ids.flatMap((id) => [
                notificacionService.marcarComoLeida(id),
                notificacionService.marcarComoLeidaUsuario(id),
            ]));
        } catch (e) {
            // silencioso
        } finally {
            setAlerta(null);
            toastMostradoRef.current = false;
            procesandoRef.current = false;
        }
    };

    const reconectar = () => {
        navigate('/admin/whatsapp');
        cerrar();
    };

    // Usuario no privilegiado: cartel chico vía react-toastify (no se autocierra).
    useEffect(() => {
        if (!alerta || esPrivilegiado || toastMostradoRef.current) return;
        toastMostradoRef.current = true;
        toast.warn(
            () => (
                <div>
                    <strong>WhatsApp Desconectado</strong>
                    <div style={{ fontSize: '0.85rem', marginTop: 2 }}>Avisar al encargado.</div>
                </div>
            ),
            {
                autoClose: false,
                closeOnClick: false,
                onClose: cerrar,
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alerta, esPrivilegiado]);

    if (!alerta || !esPrivilegiado) return null;

    // Usuario privilegiado (ADMIN/PROGRAMADOR): modal grande centrado, con la mascota.
    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
            }}
        >
            <div
                style={{
                    position: 'relative', background: '#fff', borderRadius: 16, padding: '20px 24px 28px',
                    maxWidth: 420, width: '90%', textAlign: 'center',
                    boxShadow: 'var(--shadow-lg)', border: '1px solid #e5e7eb',
                }}
            >
                <button
                    type="button"
                    onClick={cerrar}
                    aria-label="Cerrar"
                    style={{
                        position: 'absolute', top: 12, right: 12,
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#dc2626', fontSize: '1.1rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                    <i className="bi bi-x-lg" />
                </button>

                <img
                    src={imgDesconectado}
                    alt="WhatsApp desconectado"
                    style={{ width: '100%', maxWidth: 320, borderRadius: 12, margin: '8px auto 16px', display: 'block' }}
                />

                <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8, fontSize: '1.25rem' }}>
                    WhatsApp Desconectado
                </h4>
                <p style={{ color: '#64748b', marginBottom: 24, fontSize: '0.92rem', lineHeight: 1.5 }}>
                    El WhatsApp del estudio se desconectó. Hay que reconectarlo para que los mensajes sigan saliendo.
                </p>

                <button
                    className="btn btn-primary w-100"
                    onClick={reconectar}
                    style={{ borderRadius: 10, padding: '0.65rem', fontWeight: 600 }}
                >
                    <i className="bi bi-arrow-repeat me-2" />
                    Reconectar WhatsApp
                </button>
            </div>
        </div>
    );
}

export default WhatsAppDesconexionAlert;
